/**
 * Рабочее место руководителя — общее тело раздела (задача 71 §5.x + §6.2).
 *
 * Композиция недельной полосы + лёгкого заголовка дня + плашки «Не сдали» +
 * ленты карточек + дока внесения/правки + диалога отсутствия. Вынесено из
 * `TimeApprovalPage`, чтобы один и тот же флоу работал и на десктопе (D6,
 * внутри `AppShell`), и на мобильном (M6, внутри `FieldShell`-подобного
 * layout): различается только рамка, не логика/данные.
 *
 * `density="mobile"` — единственная колонка (лента в 1 ряд, не 2): на телефоне
 * «3 экрана» макапа достигаются переходами-оверлеями (`EntryDock` fullscreen на
 * мобильном = M-3, день-редактор с табелем; раскрытая плашка = M-2), а не
 * параллельными роутами. Тот же `density` пробрасывается в `EntryDock` как
 * `surface` (задача 79 §2.2): mobile = fullscreen + крупные тач-таргеты,
 * desktop = правый Sheet + компактная плотность. Паттерны —
 * `patterns.md §Рабочее место руководителя`.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { addDays, format, parseISO } from "date-fns";
import { ru as ruLocale, enUS } from "date-fns/locale";

import { Surface } from "@/components/ui/surface";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { WeekStrip } from "@/components/approval/WeekStrip";
import { ReportFeed } from "@/components/approval/ReportFeed";
import { MissingPanel } from "@/components/approval/MissingPanel";
import { EntryDock, type EntryDockSubject } from "@/components/approval/EntryDock";
import { AbsenceFormDialog } from "@/components/people/AbsenceFormDialog";
import { PeopleNavProvider } from "@/components/people/PeopleNavContext";
import { useDayFeed, useMissing, useWeekSummary } from "@/hooks/useApproval";
import { useAdminTimesheetWorkers } from "@/hooks/useAdminTimesheet";
import { useSession } from "@/store/session";
import type { MissingSubject } from "@/api/approval";

export interface ApprovalConsoleHandle {
  /** Открыть док «Моё время» (self-entry мастера) на текущем дне. */
  openMyTime: () => void;
}

interface ApprovalConsoleProps {
  weekStart: string;
  selectedDate: string;
  /** Подразделение, уже разрешённое route-host или `ApprovalLens`. */
  selectedUnitId: number | null;
  includeChildren?: boolean;
  /** Query gate: false, пока scope и выбор подразделения не разрешены. */
  queriesEnabled: boolean;
  /** Показывает приглашение выбрать подразделение вместо загрузки read-model. */
  needsUnit: boolean;
  onWeekStartChange: (weekStart: string) => void;
  onSelectedDateChange: (date: string) => void;
  density?: "desktop" | "mobile";
  /**
   * Сотрудник, чью карточку дня открыть на mount (deep-link из аудита /
   * редиректа со старого `/admin/timesheet-backfill?user_id=…`). Открывается
   * один раз — дальше навигация идёт через селектор / клик по плашке.
   */
  openUserId?: number | null;
}

export const ApprovalConsole = React.forwardRef<ApprovalConsoleHandle, ApprovalConsoleProps>(
  function ApprovalConsole(
    {
      weekStart,
      selectedDate,
      selectedUnitId,
      includeChildren = false,
      queriesEnabled,
      needsUnit,
      onWeekStartChange,
      onSelectedDateChange,
      density = "desktop",
      openUserId = null,
    },
    ref,
  ) {
    const { t, i18n } = useTranslation();
    const currentUserId = useSession((s) => s.user?.id ?? null);
    const currentUserName = useSession((s) => s.user?.display_name ?? "");
    const {
      data: week,
      isLoading,
      isError,
      error,
    } = useWeekSummary(weekStart, selectedUnitId, includeChildren, queriesEnabled);
    const { data: feed } = useDayFeed(
      selectedDate,
      selectedUnitId,
      includeChildren,
      queriesEnabled,
    );
    const { data: missing } = useMissing(
      selectedDate,
      selectedUnitId,
      includeChildren,
      queriesEnabled,
    );

    // Список сотрудников нужен deep-link-эффекту ниже (резолв имени/должности
    // по `openUserId` при входе из аудита / редиректа). Свободный селектор
    // «Сотрудник» вынесен в отдельный таб «Заполнить время» (задача 85 §5.3) —
    // вход в день из консоли остаётся через клик по плашке + «Не сдали».
    const workersQuery = useAdminTimesheetWorkers();

    const [dockSubject, setDockSubject] = React.useState<EntryDockSubject | null>(null);
    const [absenceSubject, setAbsenceSubject] = React.useState<MissingSubject | null>(null);

    React.useImperativeHandle(ref, () => ({
      openMyTime: () => {
        if (currentUserId !== null) {
          setDockSubject({ userId: currentUserId, name: currentUserName, isEdit: false });
        }
      },
    }));

    const shiftWeek = (deltaDays: number) => {
      onWeekStartChange(format(addDays(parseISO(weekStart), deltaDays), "yyyy-MM-dd"));
    };

    const selectedDay = week?.days.find((d) => d.date === selectedDate) ?? week?.days[0] ?? null;

    const openEnter = (subject: MissingSubject) =>
      setDockSubject({
        userId: subject.id,
        name: subject.full_name,
        position: subject.position,
        isEdit: false,
      });
    const openEdit = (userId: number) => {
      const card = feed?.cards.find((c) => c.user.id === userId);
      setDockSubject({
        userId,
        name: card?.user.display_name ?? "",
        position: card?.user.position,
        isEdit: true,
      });
    };

    // Deep-link: открыть карточку дня сотрудника на mount (редирект со старого
    // `/admin/timesheet-backfill?user_id=…` + «открыть в редакторе» из аудита).
    // Ждём список сотрудников; срабатывает один раз на каждый openUserId.
    const openedUserRef = React.useRef<number | null>(null);
    React.useEffect(() => {
      if (openUserId === null || workersQuery.data === undefined) return;
      if (openedUserRef.current === openUserId) return;
      const worker = workersQuery.data.find((w) => w.id === openUserId);
      if (!worker) return;
      openedUserRef.current = openUserId;
      const card = feed?.cards.find((c) => c.user.id === worker.id);
      setDockSubject({
        userId: worker.id,
        name: worker.display_name,
        position: worker.position ?? worker.unit_name ?? null,
        isEdit: card !== undefined,
      });
    }, [openUserId, workersQuery.data, feed]);

    return (
      // Имя сотрудника в карточках учёта — ссылка на `/people/$id` только на
      // десктопе (задача 82 §1.3): мобильная карточка человека не адаптирована.
      // Одна точка принятия на уже существующем `density`; примитив
      // `PersonNameLink` сам читает контекст и рендерит link/span.
      <PeopleNavProvider enabled={density === "desktop"}>
        <div className="flex flex-col gap-4">
          {needsUnit ? (
            <Surface className="p-4 text-sm text-[var(--fg-3)]" data-testid="approval-no-unit">
              {t("timeApproval.unitPicker.empty")}
            </Surface>
          ) : isError ? (
            <Surface
              className="p-4 text-sm text-[var(--tone-danger-fg)]"
              data-testid="approval-error"
            >
              <ApiErrorNotice error={error} fallbackMessage={t("common.apiError.fallback")} />
            </Surface>
          ) : isLoading || !week ? (
            <Surface className="p-4 text-sm text-[var(--fg-3)]" data-testid="approval-loading">
              {t("common.loading")}
            </Surface>
          ) : (
            <>
              <WeekStrip
                week={week}
                density={density}
                selectedDate={selectedDate}
                onSelectDate={onSelectedDateChange}
                onPrevWeek={() => shiftWeek(-7)}
                onNextWeek={() => shiftWeek(7)}
              />
              {/* Лёгкий заголовок выбранного дня (не Surface-плашка): дата под
                  недельной полосой. Локаль — по resolvedLanguage (i18n.language
                  может быть «ru-RU», строгое === "ru" роняло бы дату в EN). */}
              <h2
                className="text-sm font-semibold capitalize text-[var(--fg-1)]"
                data-testid="approval-day-title"
              >
                {format(parseISO(selectedDate), "EEEE, dd.MM", {
                  locale: i18n.resolvedLanguage === "en" ? enUS : ruLocale,
                })}
              </h2>
              {missing && (
                <MissingPanel
                  subjects={missing.subjects}
                  onEnter={openEnter}
                  onAbsence={setAbsenceSubject}
                />
              )}
              {feed && (
                <ReportFeed
                  feed={feed}
                  date={selectedDate}
                  weekStart={weekStart}
                  unitId={selectedUnitId}
                  currentUserId={currentUserId}
                  pendingCount={selectedDay?.pending_count ?? 0}
                  approvedCount={selectedDay?.approved_count ?? 0}
                  onEdit={openEdit}
                  columns={density === "mobile" ? 1 : 2}
                />
              )}
            </>
          )}
        </div>

        <EntryDock
          subject={dockSubject}
          date={selectedDate}
          weekStart={weekStart}
          unitId={selectedUnitId}
          surface={density === "mobile" ? "mobile" : "desktop-dock"}
          onClose={() => setDockSubject(null)}
        />
        {/* Отсутствие на ДИАПАЗОН дат (задача 82 §1.2): мастер закрывает не
            только один день («не явился»), но и плановый отпуск/отгул на
            несколько дней. Тот же `AbsenceFormDialog`, что на карточке
            `/people/$id` (виды — таксономия 78 §1.1, вкл. отпуск; диапазон
            зануляет каждый день через `person_schedule`-дельту). Преднабиваем
            выбранным днём. Scope «своя бригада» — на backend: subject уже
            пришёл из `MissingPanel` (scope `directly_led`). */}
        {absenceSubject !== null && (
          <AbsenceFormDialog
            open
            userId={absenceSubject.id}
            defaultDate={selectedDate}
            onOpenChange={(v) => !v && setAbsenceSubject(null)}
          />
        )}
      </PeopleNavProvider>
    );
  },
);
